import React, { useState, useMemo, memo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AdminPanelSettings,
  LocalShipping,
  SwapHoriz,
  History,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';

interface EuaBalanceHistoryEntry {
  id: string;
  entityId: string;
  previousBalance: number;
  newBalance: number;
  changeAmount: number;
  changeType: 'ADMIN_ADJUSTMENT' | 'PROCUREMENT' | 'ALLOCATION' | 'TRANSFER' | 'CORRECTION';
  reason: string;
  updatedBy: string;
  updatedByName: string;
  timestamp: string;
}

interface EuaBalanceHistoryProps {
  currentBalance: number;
  history: EuaBalanceHistoryEntry[];
}

const getChangeTypeIcon = (type: string) => {
  switch (type) {
    case 'ADMIN_ADJUSTMENT':
      return <AdminPanelSettings />;
    case 'PROCUREMENT':
      return <LocalShipping />;
    case 'ALLOCATION':
      return <TrendingUp />;
    case 'TRANSFER':
      return <SwapHoriz />;
    default:
      return <History />;
  }
};

export const EuaBalanceHistory: React.FC<EuaBalanceHistoryProps> = memo(
  ({
    currentBalance,
    history = [], // Default to empty array if undefined/null
  }) => {
    const [activeTab, setActiveTab] = useState(0);

    // Use a stable timestamp for current time to avoid re-renders changing the chart
    const stableCurrentTime = useMemo(() => dayjs(), []);

    // Ensure history is always an array
    const safeHistory = useMemo(() => (Array.isArray(history) ? history : []), [history]);

    const recentChanges = useMemo(() => safeHistory.slice(0, 10), [safeHistory]);

    const lastMonthChange = useMemo(
      () =>
        safeHistory
          .filter((entry) => dayjs(entry.timestamp).isAfter(dayjs().subtract(30, 'day')))
          .reduce((sum, entry) => sum + entry.changeAmount, 0),
      [safeHistory]
    );

    const historyColumns: GridColDef[] = useMemo(
      () => [
        {
          field: 'timestamp',
          headerName: 'Date',
          width: 180,
          valueFormatter: (value: string) => dayjs(value).format('MM/DD/YYYY HH:mm'),
        },
        { field: 'updatedByName', headerName: 'Updated By', width: 150 },
        {
          field: 'changeType',
          headerName: 'Type',
          width: 140,
          renderCell: (params) => (
            <Chip label={params.value.replace('_', ' ')} size="small" variant="outlined" />
          ),
        },
        {
          field: 'changeAmount',
          headerName: 'Change',
          width: 120,
          renderCell: (params) => (
            <Chip
              label={`${params.value > 0 ? '+' : ''}${params.value}`}
              color={params.value > 0 ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
          ),
        },
        {
          field: 'newBalance',
          headerName: 'New Balance',
          width: 140,
          type: 'number',
          valueFormatter: (value: number) => `${value.toLocaleString()} EUA`,
        },
        { field: 'reason', headerName: 'Reason', flex: 1 },
      ],
      []
    );

    return (
      <Box sx={{ mt: 3 }}>
        {/* Current Balance Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" color="textSecondary">
                  Current EUA Balance
                </Typography>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                  {currentBalance.toLocaleString()} EUA
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="textSecondary">
                  Last 30 days
                </Typography>
                <Chip
                  label={`${lastMonthChange > 0 ? '+' : ''}${lastMonthChange} EUA`}
                  color={
                    lastMonthChange > 0 ? 'success' : lastMonthChange < 0 ? 'error' : 'default'
                  }
                  icon={lastMonthChange > 0 ? <TrendingUp /> : <TrendingDown />}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs for different views */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Recent Activity" />
              <Tab label="Balance Chart" />
              <Tab label="Full History" />
            </Tabs>
          </Box>

          <CardContent>
            {/* Recent Activity List */}
            {activeTab === 0 && (
              <>
                {recentChanges.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No balance history available
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {recentChanges.map((entry, index) => (
                      <React.Fragment key={entry.id}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 48 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: entry.changeAmount > 0 ? 'success.main' : 'error.main',
                                color: 'white',
                                mr: 2,
                                '& svg': {
                                  fontSize: 20,
                                  color: 'inherit',
                                },
                              }}
                            >
                              {getChangeTypeIcon(entry.changeType)}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            slotProps={{
                              primary: {
                                component: 'div',
                              },
                              secondary: {
                                component: 'div',
                              },
                            }}
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="h6" component="span">
                                  {entry.changeAmount > 0 ? '+' : ''}
                                  {entry.changeAmount} EUA
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  component="span"
                                >
                                  {dayjs(entry.timestamp).format('MM/DD/YYYY')}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="textSecondary" component="div">
                                  {entry.reason}
                                </Typography>
                                <Box
                                  sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
                                >
                                  <Typography variant="caption" component="span">
                                    By: {entry.updatedByName}
                                  </Typography>
                                  <Typography variant="caption" component="span">
                                    New Balance: {entry.newBalance.toLocaleString()} EUA
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentChanges.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </>
            )}

            {/* Balance Chart */}
            {activeTab === 1 && (
              <Box sx={{ height: 400, width: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  EUA Balance Trend
                </Typography>
                {(() => {
                  // Prepare chart data - include current balance as latest point if history exists
                  // or show current balance as single point if no history
                  let chartData = [];
                  let timeData = [];

                  if (safeHistory.length === 0) {
                    // No history - show current balance as a single point
                    timeData = [stableCurrentTime.toDate()];
                    chartData = [currentBalance];
                  } else {
                    // Sort history by timestamp (oldest first for proper line chart)
                    const sortedHistory = [...safeHistory].sort((a, b) =>
                      dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1
                    );

                    timeData = sortedHistory.map((entry) => dayjs(entry.timestamp).toDate());
                    chartData = sortedHistory.map((entry) => entry.newBalance);

                    // Add current time/balance as the latest point if the last history entry
                    // doesn't match the current balance (in case of data inconsistency)
                    const lastEntry = sortedHistory[sortedHistory.length - 1];
                    if (lastEntry && lastEntry.newBalance !== currentBalance) {
                      timeData.push(stableCurrentTime.toDate());
                      chartData.push(currentBalance);
                    }
                  }

                  return (
                    <LineChart
                      xAxis={[
                        {
                          id: 'time',
                          data: timeData,
                          scaleType: 'time',
                          valueFormatter: (value) => dayjs(value).format('MM/DD/YYYY'),
                        },
                      ]}
                      series={[
                        {
                          id: 'balance',
                          label: 'EUA Balance',
                          data: chartData,
                          color: '#1976d2',
                        },
                      ]}
                      width={800}
                      height={350}
                      margin={{ left: 80, right: 20, top: 20, bottom: 60 }}
                      grid={{ vertical: true, horizontal: true }}
                    />
                  );
                })()}
              </Box>
            )}

            {/* Full History Table */}
            {activeTab === 2 && (
              <Box sx={{ height: 500, width: '100%' }}>
                <DataGrid
                  rows={safeHistory}
                  columns={historyColumns}
                  pageSizeOptions={[10, 25, 50]}
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25 },
                    },
                    sorting: {
                      sortModel: [{ field: 'timestamp', sort: 'desc' }],
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }
);
